import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, Clock } from 'lucide-react';
import { getPost } from '../api/client';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppFloat from '../components/WhatsAppFloat';
import SEOHead from '../components/SEOHead';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getPost(slug)
      .then(setPost)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const readTime = (content) => {
    if (!content) return '2 menit';
    return `${Math.max(1, Math.ceil(content.split(' ').length / 200))} menit`;
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', cursor: 'none' }}>
      {post && (
        <SEOHead
          title={post.title}
          description={post.excerpt || post.content?.substring(0, 160)}
          image={post.cover_image}
          url={`/blog/${post.slug}`}
          type="article"
          article={post}
        />
      )}
      <Navbar />
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '120px 32px 80px' }}>

        <Link to='/blog' style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#6a6a88', textDecoration: 'none', letterSpacing: 1, marginBottom: 48, transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = '#f2f2ff'}
          onMouseLeave={e => e.currentTarget.style.color = '#6a6a88'}>
          <ArrowLeft size={13} /> KEMBALI KE BLOG
        </Link>

        {loading && (
          <div style={{ textAlign: 'center', padding: 80, color: '#6a6a88', fontFamily: "'DM Mono', monospace", fontSize: 13 }}>MEMUAT...</div>
        )}

        {notFound && (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 28, color: '#f2f2ff', marginBottom: 12 }}>Artikel Tidak Ditemukan</div>
            <Link to='/blog' style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#00e5ff', textDecoration: 'none' }}>Kembali ke Blog</Link>
          </div>
        )}

        {post && (
          <article style={{ animation: 'fadeUp 0.6s ease' }}>
            {/* Meta */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
              {post.category && (
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#00e5ff', background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.2)', borderRadius: 2, padding: '4px 10px', letterSpacing: 1 }}>{post.category.toUpperCase()}</span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Calendar size={12} color='#6a6a88' />
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#6a6a88' }}>{formatDate(post.created_at)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Clock size={12} color='#6a6a88' />
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#6a6a88' }}>{readTime(post.content)} baca</span>
              </div>
            </div>

            {/* Title */}
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 'clamp(28px,5vw,52px)', color: '#f2f2ff', margin: '0 0 24px', letterSpacing: '-1.5px', lineHeight: 1.15 }}>
              {post.title}
            </h1>

            {/* Cover image */}
            {post.cover_image && (
              <div style={{ marginBottom: 40, borderRadius: 4, overflow: 'hidden' }}>
                <img loading="lazy" src={post.cover_image} alt={post.title} style={{ width: '100%', maxHeight: 400, objectFit: 'cover', filter: 'brightness(0.85)' }} />
              </div>
            )}

            {/* Divider */}
            <div style={{ height: 1, background: 'linear-gradient(90deg, #00e5ff, #9b00ff, transparent)', marginBottom: 40, opacity: 0.4 }} />

            {/* Content */}
            <div style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16, color: '#9090a8',
              lineHeight: 1.9,
              whiteSpace: 'pre-wrap',
            }}>
              {post.content}
            </div>

            {/* Footer */}
            <div style={{ marginTop: 64, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Link to='/blog' style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#6a6a88', textDecoration: 'none', letterSpacing: 1, transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#00e5ff'}
                onMouseLeave={e => e.currentTarget.style.color = '#6a6a88'}>
                <ArrowLeft size={13} /> ARTIKEL LAINNYA
              </Link>
              <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 3, background: 'linear-gradient(135deg,#00e5ff,#9b00ff)', color: '#07070f', fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 11, border: 'none', cursor: 'none', letterSpacing: 1, transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                KONSULTASI GRATIS
              </button>
            </div>
          </article>
        )}
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
